// src/pages/Client/MyReservations.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, Scissors, CreditCard, AlertCircle } from 'lucide-react';
import bookingService from '../../services/bookingService';
import { formatPriceFCFA } from '../../utils/formatters';
import ReservationDetailModal from '../../components/Reservations/ReservationDetailModal';
import './MyReservations.css';

const MyReservations = () => {
  const navigate = useNavigate();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('ALL');
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => { loadReservations(); }, []);

  const loadReservations = async () => {
    setLoading(true);
    try {
      const result = await bookingService.getMyReservations();
      if (result.success) setReservations(result.reservations);
    } catch (error) {
      console.error('Erreur chargement réservations:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredReservations = reservations.filter(res => {
    if (filter === 'ALL') return true;
    if (filter === 'EN_ATTENTE') {
      if (res.status !== 'EN_ATTENTE') return false;
      const dateStr = String(res.reservation_date || '').slice(0, 10);
      const timeStr = String(res.reservation_time || '00:00').slice(0, 5);
      const when = new Date(`${dateStr}T${timeStr}:00`);
      if (Number.isNaN(when.getTime())) return true;
      return when.getTime() >= Date.now();
    }
    return res.status === filter;
  });

  const handleCardClick = (reservation) => {
    setSelectedReservation(reservation);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedReservation(null);
  };

  const handleActionComplete = () => {
    loadReservations();
    handleCloseModal();
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const days = ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'];
    const months = ['jan', 'fév', 'mar', 'avr', 'mai', 'juin', 'juil', 'août', 'sep', 'oct', 'nov', 'déc'];
    return `${days[date.getDay()]} ${date.getDate()} ${months[date.getMonth()]}`;
  };

  const getStatusBadge = (status, reservation) => {
    if (status === 'EN_ATTENTE' && reservation?.created_by === 'CLIENT') {
      return { label: 'Finaliser le paiement', class: 'status-pending-payment', icon: <CreditCard size={12} /> };
    }
    const badges = {
      EN_ATTENTE: { label: 'En attente',  class: 'status-pending',   icon: <Clock size={12} />     },
      CONFIRMEE:  { label: 'Confirmé',    class: 'status-confirmed', icon: null                    },
      TERMINEE:   { label: 'Terminé',     class: 'status-completed', icon: null                    },
      ANNULEE:    { label: 'Annulé',      class: 'status-cancelled', icon: null                    },
      REFUSEE:    { label: 'Refusé',      class: 'status-refused',   icon: <AlertCircle size={12} />},
    };
    return badges[status] || { label: status, class: 'status-default', icon: null };
  };

  const getTotalDuration = (reservation) => {
    if (reservation.services_details) {
      const services = typeof reservation.services_details === 'string'
        ? JSON.parse(reservation.services_details)
        : reservation.services_details;
      const totalMin = services.reduce((sum, s) => sum + (s.duration || 0), 0);
      const hours = Math.floor(totalMin / 60);
      const mins  = totalMin % 60;
      if (hours > 0 && mins > 0) return `${hours}h${mins}`;
      if (hours > 0) return `${hours}h`;
      return `${mins}min`;
    }
    return reservation.service_duration ? `${reservation.service_duration}min` : 'N/A';
  };

  const getServiceDisplay = (reservation) => {
    if (reservation.services_details) {
      const services = typeof reservation.services_details === 'string'
        ? JSON.parse(reservation.services_details)
        : reservation.services_details;
      return services.length === 1
        ? services[0].name
        : `${services[0].name} (+${services.length - 1})`;
    }
    return reservation.service_name || 'Service';
  };

  const counts = {
    ALL:       reservations.length,
    EN_ATTENTE: reservations.filter(r => r.status === 'EN_ATTENTE').length,
    CONFIRMEE:  reservations.filter(r => r.status === 'CONFIRMEE').length,
    TERMINEE:   reservations.filter(r => r.status === 'TERMINEE').length,
  };

  if (loading) {
    return (
      <div className="my-reservations-page">
        <div className="loading-container">
          <div className="spinner" />
          <p>Chargement de vos réservations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="my-reservations-page">
      <div className="reservations-container">

        <div className="reservations-header">
          <h1>Mes Réservations</h1>
          <p className="subtitle">{counts.ALL} réservation{counts.ALL > 1 ? 's' : ''}</p>
        </div>

        <div className="filters">
          {[
            { key: 'ALL',       label: 'Toutes',     count: counts.ALL       },
            { key: 'EN_ATTENTE',label: 'En attente', count: counts.EN_ATTENTE },
            { key: 'CONFIRMEE', label: 'Confirmées', count: counts.CONFIRMEE  },
            { key: 'TERMINEE',  label: 'Terminées',  count: counts.TERMINEE   },
          ].map(f => (
            <button
              key={f.key}
              className={`filter-btn ${filter === f.key ? 'active' : ''}`}
              onClick={() => setFilter(f.key)}
            >
              {f.label}{f.count > 0 && ` (${f.count})`}
            </button>
          ))}
        </div>

        {filteredReservations.length === 0 ? (
          <div className="empty-state">
            <Calendar size={48} strokeWidth={1.5} style={{ opacity: 0.3 }} />
            <h3>Aucune réservation</h3>
            <p>
              {filter === 'ALL'
                ? "Vous n'avez pas encore de réservation"
                : `Aucune réservation "${getStatusBadge(filter, null).label.toLowerCase()}"`
              }
            </p>
            <button className="btn-primary" onClick={() => navigate('/')}>
              Réserver maintenant
            </button>
          </div>
        ) : (
          <div className="reservations-list">
            {filteredReservations.map((reservation) => {
              const status = getStatusBadge(reservation.status, reservation);
              return (
                <div
                  key={reservation.id}
                  className="reservation-card"
                  onClick={() => handleCardClick(reservation)}
                >
                  <h3 className="salon-name">{reservation.salon_name}</h3>

                  <div className="card-info">
                    <Calendar size={14} />
                    <span className="info-item">
                      {formatDate(reservation.reservation_date)} · {reservation.reservation_time?.slice(0, 5)}
                    </span>
                  </div>

                  <div className="card-info">
                    <Scissors size={14} />
                    <span className="info-item">{getServiceDisplay(reservation)}</span>
                  </div>

                  <div className="card-footer">
                    <span className="duration">
                      <Clock size={13} /> {getTotalDuration(reservation)}
                    </span>
                    <span className="price">{formatPriceFCFA(reservation.total_price)}</span>
                  </div>

                  <div className={`status-badge ${status.class}`}>
                    {status.icon && <span style={{ marginRight: 4, display: 'flex' }}>{status.icon}</span>}
                    {status.label}
                  </div>
                </div>
              );
            })}
          </div>
        )}

      </div>

      {showModal && selectedReservation && (
        <ReservationDetailModal
          reservation={selectedReservation}
          onClose={handleCloseModal}
          onActionComplete={handleActionComplete}
        />
      )}
    </div>
  );
};

export default MyReservations;
